import { Suspense, lazy, useEffect, Component, type ReactNode, type ErrorInfo } from "react";
import RouteSeo from "./components/RouteSeo";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Layout from "./components/Layout";

/**
 * After a frontend deploy, a still-running tab / Capacitor WebView holds the
 * OLD index.html, whose route map references lazy chunks by old content
 * hashes (e.g. LandingPage-abc123.js). The new deploy emits chunks with new
 * hashes; the old filenames 404. Without an error boundary that 404 throws
 * inside Suspense and React unmounts the whole tree → blank screen. The
 * WebView has no URL bar to "just refresh", so the user is stuck.
 *
 * This boundary catches the ChunkLoadError signature and force-reloads the
 * page, which fetches fresh HTML + the current chunk hashes and self-heals.
 * Everything else bubbles up.
 */
class ChunkErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(err: unknown): { failed: boolean } {
    const name = (err as { name?: string })?.name;
    const message = (err as { message?: string })?.message ?? "";
    const isChunkError =
      name === "ChunkLoadError" ||
      /Loading chunk \d+ failed/i.test(message) ||
      /Loading CSS chunk/i.test(message) ||
      /Failed to fetch dynamically imported module/i.test(message);
    if (isChunkError) {
      // Reload from scratch — the next paint will use the current bundle.
      // `location.reload` is fine in BrowserRouter; Capacitor WebView treats
      // it as a normal navigation.
      window.location.reload();
      return { failed: true };
    }
    // Not our problem — let it propagate to whatever's above (or surface as
    // an unhandled boundary so we see it instead of swallowing real bugs).
    throw err;
  }

  componentDidCatch(_err: unknown, _info: ErrorInfo): void {
    // No-op: we either reload (handled in getDerivedStateFromError) or
    // re-throw. Override is required because we set state from the static
    // helper above.
  }

  render(): ReactNode {
    // While the reload is in flight, render nothing rather than the broken
    // tree; the reload happens in the same task tick so this is brief.
    if (this.state.failed) return null;
    return this.props.children;
  }
}

// Lazy-loaded pages — each becomes its own async chunk
const LandingPage              = lazy(() => import("./pages/LandingPage"));
const PreviewPage              = lazy(() => import("./pages/PreviewPage"));
const DownloadPage             = lazy(() => import("./pages/DownloadPage"));
const AboutPage                = lazy(() => import("./pages/AboutPage"));
const ContactPage              = lazy(() => import("./pages/ContactPage"));
const TermsPage                = lazy(() => import("./pages/TermsPage"));
const PrivacyPage              = lazy(() => import("./pages/PrivacyPage"));
const MadLibsPage              = lazy(() => import("./pages/MadLibsPage"));
const MadLibsPlayPage          = lazy(() => import("./pages/MadLibsPlayPage"));
const BabyShowerPage           = lazy(() => import("./pages/BabyShowerPage"));
const BridalShowerPage         = lazy(() => import("./pages/BridalShowerPage"));
const WouldYouRatherPage       = lazy(() => import("./pages/WouldYouRatherPage"));
const ConversationStartersPage = lazy(() => import("./pages/ConversationStartersPage"));
const TruthOrDarePage          = lazy(() => import("./pages/TruthOrDarePage"));
const NeverHaveIEverPage       = lazy(() => import("./pages/NeverHaveIEverPage"));
const IcebreakerPage           = lazy(() => import("./pages/IcebreakerPage"));
const EmojiPictionaryPage      = lazy(() => import("./pages/EmojiPictionaryPage"));
const HolidayGamesPage         = lazy(() => import("./pages/HolidayGamesPage"));
const DrinkingGamesPage        = lazy(() => import("./pages/DrinkingGamesPage"));
const ScavengerHuntPage        = lazy(() => import("./pages/ScavengerHuntPage"));
const TriviaPage               = lazy(() => import("./pages/TriviaPage"));
const WordSearchPage            = lazy(() => import("./pages/WordSearchPage"));
const CrosswordPage            = lazy(() => import("./pages/CrosswordPage"));
const CustomGamePage           = lazy(() => import("./pages/CustomGamePage"));
const BingoPage                = lazy(() => import("./pages/BingoPage"));
const CheckoutPage             = lazy(() => import("./pages/CheckoutPage"));
const PaymentPage              = lazy(() => import("./pages/PaymentPage"));
const CartDownloadPage         = lazy(() => import("./pages/CartDownloadPage"));
const BlogIndexPage            = lazy(() => import("./pages/BlogIndexPage"));
const DocsPage                 = lazy(() => import("./pages/DocsPage"));
const PromptGamesPage          = lazy(() => import("./pages/PromptGamesPage"));
const CouplesPage              = lazy(() => import("./pages/CouplesPage"));
const MemoryGamePage           = lazy(() => import("./pages/MemoryGamePage"));
const NotFoundPage             = lazy(() => import("./pages/NotFoundPage"));
const BirthdayBingoPost        = lazy(() => import("./pages/blog/BirthdayBingoPost"));
const ClassroomWordSearchPost  = lazy(() => import("./pages/blog/ClassroomWordSearchPost"));
const HolidayPartyGamesPost    = lazy(() => import("./pages/blog/HolidayPartyGamesPost"));
const ScavengerHuntBirthdayPost = lazy(() => import("./pages/blog/ScavengerHuntBirthdayPost"));
const BabyShowerBingoPost      = lazy(() => import("./pages/blog/BabyShowerBingoPost"));
const OfficeHolidayPartyGamesPost = lazy(() => import("./pages/blog/OfficeHolidayPartyGamesPost"));
const WeddingMadLibsPost       = lazy(() => import("./pages/blog/WeddingMadLibsPost"));

function PageSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Map route paths to game labels for click tracking
const GAME_ROUTES: Record<string, string> = {
  "/bingo":           "bingo",
  "/word-search":     "wordsearch",
  "/crosswords":      "crossword",
  "/scavenger-hunt":  "scavenger",
  "/mad-libs":        "madlibs",
  "/holiday-games":   "holiday",
  "/drinking-games":  "drinking",
  "/baby-shower":     "baby-shower",
  "/bridal-shower":   "bridal-shower",
  "/trivia-pdf":      "trivia",
  "/custom-game":     "custom",
  "/emoji-pictionary":"emoji-pictionary",
  "/would-you-rather":"would-you-rather",
  "/truth-or-dare":   "truth-or-dare",
  "/never-have-i-ever":"never-have-i-ever",
  "/icebreaker":      "icebreaker",
};

// Scroll to a #hash anchor when client-side navigation lands on one.
// Without this, `<Navigate to="/#faq">` (the /faq → landing redirect) wins
// the URL change but the browser never scrolls — react-router doesn't fire a
// native hash-change event. Run on every location update so `<a href="#x">`
// jumps within the page still feel native, and add a small RAF so the target
// section is mounted before we try to scroll.
function ScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [location.pathname, location.hash]);
  return null;
}

// Fire-and-forget page view tracking — no third-party scripts
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    // Only track in production (no noise from local dev)
    if (import.meta.env.DEV) return;
    fetch("/api/pageview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        page: location.pathname,
        referrer: document.referrer || undefined,
      }),
      keepalive: true,
    }).catch(() => {});

    // Track game page clicks
    const game = GAME_ROUTES[location.pathname];
    if (game) {
      fetch("/api/track", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event: "click", game }),
        keepalive: true,
      }).catch(() => {});
    }
  }, [location.pathname]);
  return null;
}

export default function App() {
  return (
    <Router>
      <PageViewTracker />
      <ScrollToHash />
      <RouteSeo />
      <ChunkErrorBoundary>
      <Suspense fallback={<PageSpinner />}>
        <Routes>
          {/* Landing page — standalone, has its own nav and footer */}
          <Route path="/" element={<LandingPage />} />

          {/* Mad Libs — standalone page with its own nav */}
          <Route path="/madlibs" element={<MadLibsPage />} />
          <Route path="/mad-libs" element={<Navigate to="/madlibs" replace />} />
          <Route path="/madlibs/play/:bundleId/:storyIndex" element={<MadLibsPlayPage />} />

          {/* Shower bundles — standalone pages with their own nav */}
          <Route path="/baby-shower" element={<BabyShowerPage />} />
          <Route path="/bridal-shower" element={<BridalShowerPage />} />

          {/* Card games — standalone pages with their own nav */}
          <Route path="/would-you-rather" element={<WouldYouRatherPage />} />
          <Route path="/conversation-starters" element={<ConversationStartersPage />} />
          <Route path="/truth-or-dare" element={<TruthOrDarePage />} />
          <Route path="/never-have-i-ever" element={<NeverHaveIEverPage />} />

          {/* Corporate & Party games — standalone pages with their own nav */}
          <Route path="/icebreakers" element={<IcebreakerPage />} />
          <Route path="/emoji-pictionary" element={<EmojiPictionaryPage />} />

          {/* Holiday & Drinking games — standalone pages with their own nav */}
          <Route path="/holiday-games" element={<HolidayGamesPage />} />
          <Route path="/drinking-games" element={<DrinkingGamesPage />} />

          {/* Scavenger Hunt, Trivia, Word Search — standalone pages with their own nav */}
          <Route path="/scavenger-hunt" element={<ScavengerHuntPage />} />
          <Route path="/trivia-pdf" element={<TriviaPage />} />
          <Route path="/word-search" element={<WordSearchPage />} />
          <Route path="/crosswords" element={<CrosswordPage />} />
          <Route path="/custom" element={<CustomGamePage />} />

          {/* Party Game Bundle was removed (Jake: "do not re-add") — buyers build their
              own bundle via the cart. Old links land on the catalog. */}
          <Route path="/party-games" element={<Navigate to="/#catalog" replace />} />

          {/* Bingo — standalone */}
          <Route path="/bingo" element={<BingoPage />} />

          {/* /trivia + /trivia-night dropped 2026-05-26 — gated trivia is no longer part of PrintPartyHub. Catch-all below handles fall-through. */}

          {/* New game types */}
          <Route path="/prompt-games" element={<PromptGamesPage />} />
          <Route path="/couples" element={<CouplesPage />} />
          <Route path="/memory-game" element={<MemoryGamePage />} />

          {/* Checkout — standalone */}
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/pay" element={<PaymentPage />} />

          {/* App pages wrapped in Layout (top nav, footer) */}
          <Route path="/preview/:sessionId" element={<Layout><PreviewPage /></Layout>} />
          <Route path="/download/cart" element={<Layout><CartDownloadPage /></Layout>} />
          <Route path="/download/:sessionId" element={<Layout><DownloadPage /></Layout>} />
          <Route path="/about" element={<Layout><AboutPage /></Layout>} />
          {/* /faq moved into the landing page (anchor #faq) on 2026-05-25.
              Redirect preserves the URL for footer / inbound / SEO links. */}
          <Route path="/faq" element={<Navigate to="/#faq" replace />} />
          <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
          <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
          <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />

          {/* Docs */}
          <Route path="/docs" element={<DocsPage />} />

          {/* Blog */}
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/birthday-party-bingo-ideas" element={<BirthdayBingoPost />} />
          <Route path="/blog/classroom-word-search-activities" element={<ClassroomWordSearchPost />} />
          <Route path="/blog/holiday-party-games-printable" element={<HolidayPartyGamesPost />} />
          <Route path="/blog/scavenger-hunt-ideas-kids-birthday" element={<ScavengerHuntBirthdayPost />} />
          <Route path="/blog/baby-shower-bingo-printable" element={<BabyShowerBingoPost />} />
          <Route path="/blog/office-holiday-party-games-printable" element={<OfficeHolidayPartyGamesPost />} />
          <Route path="/blog/wedding-mad-libs-for-guests" element={<WeddingMadLibsPost />} />

          {/* 404 catch-all */}
          {/* Unknown paths: a real 404 page (server returns HTTP 404 too) — not a silent redirect home. */}
          <Route path="/trivia" element={<Navigate to="/trivia-pdf" replace />} />
          <Route path="/trivia-night" element={<Navigate to="/trivia-pdf" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      </ChunkErrorBoundary>
    </Router>
  );
}
